import React from "react";
import { Button, Form } from "react-bootstrap";

function emptyMarketingTheatreRow() {
  return {
    key: "",
    name: "",
    imageUrl: "",
    theatreVideoUrl: "",
    branchVideoUrl: "",
    location: "",
    slotsLabel: "",
    slotsTone: "gray",
    capacity: "",
    decor: "",
    price: "",
    priceText: "",
    extra: "",
    times: [],
  };
}

/** Structured editors for home slices that used to live in a JSON textarea. */
export default function AdminHomeAdvancedSections({ homeDraft, setHomeDraft, showBanner }) {
  const intros = [
    ["celebrationsIntro", "Celebrations section intro"],
    ["luxuryIntro", "Luxury / “why celebrate” intro"],
    ["advantagesIntro", "Advantages intro"],
    ["bookingIntro", "Booking teaser intro"],
    ["galleryIntro", "Gallery intro"],
  ];

  const setIntro = (prop, patch) => {
    setHomeDraft((prev) => ({
      ...prev,
      [prop]: { ...(prev[prop] || {}), ...patch },
    }));
  };

  const setCelebrationRow = (i, patch) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.celebrations || [])];
      rows[i] = { ...(rows[i] || {}), ...patch };
      return { ...prev, celebrations: rows };
    });
  };

  const addCelebrationRow = () => {
    setHomeDraft((prev) => ({
      ...prev,
      celebrations: [...(prev.celebrations || []), { title: "", text: "", imageUrl: "" }],
    }));
    showBanner("info", "New celebration row added — titles and images (Page media tab) next.");
  };

  const removeCelebrationRow = (i) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.celebrations || [])];
      rows.splice(i, 1);
      return { ...prev, celebrations: rows };
    });
  };

  const setGalleryRow = (i, patch) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.gallery || [])];
      rows[i] = { ...(rows[i] || {}), ...patch };
      return { ...prev, gallery: rows };
    });
  };

  const addGalleryRow = () => {
    setHomeDraft((prev) => ({
      ...prev,
      gallery: [...(prev.gallery || []), { title: "", imageUrl: "", big: false }],
    }));
  };

  const removeGalleryRow = (i) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.gallery || [])];
      rows.splice(i, 1);
      return { ...prev, gallery: rows };
    });
  };

  const setLuxuryRow = (i, slot, val) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.luxuryCards || [])];
      const cur = [...(rows[i] || ["", "", ""])];
      if (slot === 0) cur[0] = val;
      if (slot === 1) cur[1] = val;
      if (slot === 2) cur[2] = val;
      rows[i] = cur;
      return { ...prev, luxuryCards: rows };
    });
  };

  const addLuxuryRow = () => {
    setHomeDraft((prev) => ({
      ...prev,
      luxuryCards: [...(prev.luxuryCards || []), ["", "", ""]],
    }));
  };

  const removeLuxuryRow = (i) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.luxuryCards || [])];
      rows.splice(i, 1);
      return { ...prev, luxuryCards: rows };
    });
  };

  const setAdvRow = (i, slot, val) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.advantageCards || [])];
      const cur = [...(rows[i] || ["", "", ""])];
      if (slot === 0) cur[0] = val;
      if (slot === 1) cur[1] = val;
      if (slot === 2) cur[2] = val;
      rows[i] = cur;
      return { ...prev, advantageCards: rows };
    });
  };

  const addAdvRow = () => {
    setHomeDraft((prev) => ({
      ...prev,
      advantageCards: [...(prev.advantageCards || []), ["", "", ""]],
    }));
  };

  const removeAdvRow = (i) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.advantageCards || [])];
      rows.splice(i, 1);
      return { ...prev, advantageCards: rows };
    });
  };

  const setMarketingRow = (i, patch) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.marketingTheatres || [])];
      rows[i] = { ...(rows[i] || emptyMarketingTheatreRow()), ...patch };
      return { ...prev, marketingTheatres: rows };
    });
  };

  const addMarketingRow = () => {
    setHomeDraft((prev) => ({
      ...prev,
      marketingTheatres: [...(prev.marketingTheatres || []), emptyMarketingTheatreRow()],
    }));
  };

  const removeMarketingRow = (i) => {
    setHomeDraft((prev) => {
      const rows = [...(prev.marketingTheatres || [])];
      rows.splice(i, 1);
      return { ...prev, marketingTheatres: rows };
    });
  };

  const celebrations = homeDraft.celebrations || [];
  const gallery = homeDraft.gallery || [];
  const luxuryCards = homeDraft.luxuryCards || [];
  const advantageCards = homeDraft.advantageCards || [];
  const marketingRows = homeDraft.marketingTheatres || [];

  const foodTagsText = ((homeDraft.foodSection && homeDraft.foodSection.tags) || []).join("\n");

  return (
    <div className="mt-4 pt-3 border-top" style={{ borderColor: "rgba(148,163,184,.35)" }}>
      <h5 className="mb-3">Sections & lists</h5>
      <p className="text-muted small mb-4">
        Intros appear as section headings. Card copy and imagery paths are editable here; image/video files for
        carousels are on the Page media tab.
      </p>

      <h6 className="text-secondary mb-2">Section intros</h6>
      {intros.map(([prop, heading]) => {
        const blk = homeDraft[prop] || {};
        const showAnchorId =
          prop === "celebrationsIntro" || prop === "galleryIntro" || prop === "bookingIntro";
        return (
          <div
            key={prop}
            className="rounded p-3 mb-3"
            style={{ background: "rgba(15,23,42,.4)", border: "1px solid rgba(148,163,184,.25)" }}
          >
            <div className="fw-semibold mb-2">{heading}</div>
            {showAnchorId ? (
              <Form.Group className="mb-2">
                <Form.Label className="small text-muted mb-0">Anchor id</Form.Label>
                <Form.Control
                  size="sm"
                  value={blk.id ?? ""}
                  onChange={(e) => setIntro(prop, { id: e.target.value })}
                />
              </Form.Group>
            ) : null}
            <Form.Group className="mb-2">
              <Form.Label className="small text-muted mb-0">Eyebrow</Form.Label>
              <Form.Control
                size="sm"
                value={blk.eyebrow ?? ""}
                onChange={(e) => setIntro(prop, { eyebrow: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small text-muted mb-0">Title</Form.Label>
              <Form.Control
                size="sm"
                value={blk.title ?? ""}
                onChange={(e) => setIntro(prop, { title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="small text-muted mb-0">Body</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={blk.text ?? ""}
                onChange={(e) => setIntro(prop, { text: e.target.value })}
              />
            </Form.Group>
          </div>
        );
      })}

      <h6 className="text-secondary mt-4 mb-2">Celebration cards</h6>
      {celebrations.map((row, i) => (
        <div
          key={`celeb-${String(i)}`}
          className="border rounded p-3 mb-2"
          style={{ borderColor: "rgba(148,163,184,.35)" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small fw-bold text-secondary">Card {i + 1}</span>
            <Button variant="outline-danger" size="sm" type="button" onClick={() => removeCelebrationRow(i)}>
              Remove
            </Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Title</Form.Label>
            <Form.Control
              size="sm"
              value={row.title ?? ""}
              onChange={(e) => setCelebrationRow(i, { title: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Body</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={row.text ?? ""}
              onChange={(e) => setCelebrationRow(i, { text: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Image URL (or set via Page media)</Form.Label>
            <Form.Control
              size="sm"
              className="font-monospace"
              value={row.imageUrl ?? ""}
              onChange={(e) => setCelebrationRow(i, { imageUrl: e.target.value })}
            />
          </Form.Group>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" type="button" className="mb-4" onClick={addCelebrationRow}>
        Add celebration card
      </Button>

      <h6 className="text-secondary mb-2">Food block · copy & tags</h6>
      <div className="border rounded p-3 mb-4" style={{ borderColor: "rgba(148,163,184,.35)" }}>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Eyebrow</Form.Label>
          <Form.Control
            size="sm"
            value={homeDraft.foodSection?.eyebrow ?? ""}
            onChange={(e) =>
              setHomeDraft((p) => ({
                ...p,
                foodSection: { ...p.foodSection, eyebrow: e.target.value },
              }))
            }
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Title</Form.Label>
          <Form.Control
            size="sm"
            value={homeDraft.foodSection?.title ?? ""}
            onChange={(e) =>
              setHomeDraft((p) => ({
                ...p,
                foodSection: { ...p.foodSection, title: e.target.value },
              }))
            }
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Body</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            size="sm"
            value={homeDraft.foodSection?.body ?? ""}
            onChange={(e) =>
              setHomeDraft((p) => ({
                ...p,
                foodSection: { ...p.foodSection, body: e.target.value },
              }))
            }
          />
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-0">Tags (one per line)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            size="sm"
            value={foodTagsText}
            onChange={(e) =>
              setHomeDraft((p) => ({
                ...p,
                foodSection: {
                  ...p.foodSection,
                  tags: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                },
              }))
            }
          />
        </Form.Group>
      </div>

      <h6 className="text-secondary mb-2">Luxury feature cards ([icon, title, text])</h6>
      {luxuryCards.map((row, i) => (
        <div
          key={`lux-${String(i)}`}
          className="border rounded p-3 mb-2"
          style={{ borderColor: "rgba(148,163,184,.35)" }}
        >
          <div className="d-flex justify-content-between mb-2">
            <span className="small fw-bold text-secondary">Row {i + 1}</span>
            <Button variant="outline-danger" size="sm" type="button" onClick={() => removeLuxuryRow(i)}>
              Remove
            </Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Icon / emoji</Form.Label>
            <Form.Control
              size="sm"
              value={row?.[0] ?? ""}
              onChange={(e) => setLuxuryRow(i, 0, e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Title</Form.Label>
            <Form.Control size="sm" value={row?.[1] ?? ""} onChange={(e) => setLuxuryRow(i, 1, e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={row?.[2] ?? ""}
              onChange={(e) => setLuxuryRow(i, 2, e.target.value)}
            />
          </Form.Group>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" type="button" className="mb-4" onClick={addLuxuryRow}>
        Add luxury card row
      </Button>

      <h6 className="text-secondary mb-2">Advantage cards</h6>
      {advantageCards.map((row, i) => (
        <div
          key={`adv-${String(i)}`}
          className="border rounded p-3 mb-2"
          style={{ borderColor: "rgba(148,163,184,.35)" }}
        >
          <div className="d-flex justify-content-between mb-2">
            <span className="small fw-bold text-secondary">Row {i + 1}</span>
            <Button variant="outline-danger" size="sm" type="button" onClick={() => removeAdvRow(i)}>
              Remove
            </Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Icon / emoji</Form.Label>
            <Form.Control size="sm" value={row?.[0] ?? ""} onChange={(e) => setAdvRow(i, 0, e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Title</Form.Label>
            <Form.Control size="sm" value={row?.[1] ?? ""} onChange={(e) => setAdvRow(i, 1, e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              value={row?.[2] ?? ""}
              onChange={(e) => setAdvRow(i, 2, e.target.value)}
            />
          </Form.Group>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" type="button" className="mb-4" onClick={addAdvRow}>
        Add advantage row
      </Button>

      <h6 className="text-secondary mb-2">Gallery items</h6>
      {gallery.map((row, i) => (
        <div
          key={`gal-${String(i)}`}
          className="border rounded p-3 mb-2"
          style={{ borderColor: "rgba(148,163,184,.35)" }}
        >
          <div className="d-flex justify-content-between mb-2">
            <span className="small fw-bold text-secondary">Item {i + 1}</span>
            <Button variant="outline-danger" size="sm" type="button" onClick={() => removeGalleryRow(i)}>
              Remove
            </Button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-0">Title</Form.Label>
            <Form.Control
              size="sm"
              value={row.title ?? ""}
              onChange={(e) => setGalleryRow(i, { title: e.target.value })}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id={`gal-big-${i}`}
            className="mb-2"
            label="Featured large tile (big)"
            checked={Boolean(row.big)}
            onChange={(e) => setGalleryRow(i, { big: e.target.checked })}
          />
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Image URL</Form.Label>
            <Form.Control
              size="sm"
              className="font-monospace"
              value={row.imageUrl ?? ""}
              onChange={(e) => setGalleryRow(i, { imageUrl: e.target.value })}
            />
          </Form.Group>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" type="button" className="mb-4" onClick={addGalleryRow}>
        Add gallery item
      </Button>

      <h6 className="text-secondary mb-2">Marketing theatre cards (Home demos)</h6>
      <p className="small text-muted mb-3">
        These are editorial cards, not live inventory. Slot lines are plain text labels for display only.
      </p>
      {marketingRows.map((row, i) => (
        <div
          key={`mkt-${String(i)}-${row.key ?? ""}`}
          className="border rounded p-3 mb-3"
          style={{ borderColor: "rgba(148,163,184,.35)" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong className="text-secondary small">{row.name || row.key || `Row ${i + 1}`}</strong>
            <Button variant="outline-danger" size="sm" type="button" onClick={() => removeMarketingRow(i)}>
              Remove
            </Button>
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Key (slug)</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.key ?? ""}
                  onChange={(e) => setMarketingRow(i, { key: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-8">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Display name</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.name ?? ""}
                  onChange={(e) => setMarketingRow(i, { name: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Image URL</Form.Label>
                <Form.Control
                  size="sm"
                  className="font-monospace"
                  value={row.imageUrl ?? ""}
                  onChange={(e) => setMarketingRow(i, { imageUrl: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Theatre demo video URL</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.theatreVideoUrl ?? ""}
                  onChange={(e) => setMarketingRow(i, { theatreVideoUrl: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Branch demo video URL</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.branchVideoUrl ?? ""}
                  onChange={(e) => setMarketingRow(i, { branchVideoUrl: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Location label</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.location ?? ""}
                  onChange={(e) => setMarketingRow(i, { location: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Slots badge text</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.slotsLabel ?? ""}
                  onChange={(e) => setMarketingRow(i, { slotsLabel: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Slots tone</Form.Label>
                <Form.Select
                  size="sm"
                  value={row.slotsTone ?? "gray"}
                  onChange={(e) => setMarketingRow(i, { slotsTone: e.target.value })}
                >
                  <option value="gray">gray</option>
                  <option value="red">red</option>
                  <option value="green">green</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-12">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Capacity text</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.capacity ?? ""}
                  onChange={(e) => setMarketingRow(i, { capacity: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Decor note</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.decor ?? ""}
                  onChange={(e) => setMarketingRow(i, { decor: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Price headline</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.price ?? ""}
                  onChange={(e) => setMarketingRow(i, { price: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Price subtitle</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.priceText ?? ""}
                  onChange={(e) => setMarketingRow(i, { priceText: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Extra pricing line</Form.Label>
                <Form.Control
                  size="sm"
                  value={row.extra ?? ""}
                  onChange={(e) => setMarketingRow(i, { extra: e.target.value })}
                />
              </Form.Group>
            </div>
            <div className="col-12">
              <Form.Group className="mb-0">
                <Form.Label className="small mb-0">Display time lines (one per line)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  size="sm"
                  className="font-monospace"
                  value={(row.times || []).join("\n")}
                  onChange={(e) =>
                    setMarketingRow(i, {
                      times: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                    })
                  }
                />
              </Form.Group>
            </div>
          </div>
        </div>
      ))}
      <Button variant="outline-primary" size="sm" type="button" onClick={addMarketingRow}>
        Add marketing theatre row
      </Button>
    </div>
  );
}
